import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { EligibleTour } from '../types';

const DIGEST_LAST_SHOWN_KEY = 'feature_tour_digest_last_shown';

function getStartOfCurrentWeek(): Date {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export interface UseDigestReturn {
  isVisible: boolean;
  digestTours: EligibleTour[];
  showDigestModal: () => void;
  dismissDigest: () => Promise<void>;
}

export function useDigest(eligibleTours: EligibleTour[]): UseDigestReturn {
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [weeklyCapHit, setWeeklyCapHit] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const hasCheckedStorageRef = useRef(false);

  const digestTours = useMemo<EligibleTour[]>(
    () =>
      eligibleTours.filter(
        (t) => t.triggerType === 'LOGIN_DIGEST' || t.triggerType === 'BOTH'
      ),
    [eligibleTours]
  );

  useEffect(() => {
    if (hasCheckedStorageRef.current) return;
    hasCheckedStorageRef.current = true;

    if (__DEV__) {
      setWeeklyCapHit(false);
      return;
    }

    AsyncStorage.getItem(DIGEST_LAST_SHOWN_KEY).then((stored) => {
      if (stored === null) {
        setWeeklyCapHit(false);
        return;
      }
      const lastShown = new Date(stored);
      const weekStart = getStartOfCurrentWeek();
      if (lastShown >= weekStart) {
        setWeeklyCapHit(true);
      } else {
        setWeeklyCapHit(false);
      }
    });
  }, []);

  const isVisible =
    !sessionDismissed &&
    !weeklyCapHit &&
    digestTours.length > 0;

  const showDigestModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const dismissDigest = useCallback(async () => {
    setSessionDismissed(true);
    setIsModalVisible(false);
    const now = new Date().toISOString();
    await AsyncStorage.setItem(DIGEST_LAST_SHOWN_KEY, now);
  }, []);

  return {
    isVisible,
    digestTours,
    showDigestModal,
    dismissDigest,
  };
}
